import { normalizeText, pushDataLayerEvent } from '~/utils/eventTracking'

let teardown = null

function getTrackingContext(trackingStore) {
  return {
    tracking_cid: trackingStore.getCid(),
    utm_present: Boolean(trackingStore.getUtmParams()),
  }
}

function getClickContext(element) {
  if (element.closest('header')) {
    return 'header'
  }

  if (element.closest('footer')) {
    return 'footer'
  }

  if (element.closest('main')) {
    return 'main'
  }

  return 'document'
}

function getLinkText(link) {
  return normalizeText(link.textContent || link.getAttribute('aria-label') || link.getAttribute('title') || '')
}

function isExternalLink(link) {
  const href = link.getAttribute('href')
  if (!href) {
    return false
  }

  if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return false
  }

  try {
    const url = new URL(href, window.location.origin)
    return url.origin !== window.location.origin
  } catch {
    return false
  }
}

function isDownloadLink(link) {
  if (link.hasAttribute('download')) {
    return true
  }

  const href = (link.getAttribute('href') || '').toLowerCase()
  return /\.(pdf|doc|docx|xls|xlsx|zip|csv)$/.test(href)
}

function hasButtonClass(element) {
  return ['btn', 'btn-white', 'btn-hollow', 'fui-submit'].some(className => element.classList.contains(className))
}

function isHighValueNavLink(link) {
  return Boolean(link.closest('[aria-label="Main Navigation"], [aria-label="Mobile Navigation"], [aria-label="Footer Navigation"]'))
}

function shouldTrackLink(link) {
  return isHighValueNavLink(link) || hasButtonClass(link) || isExternalLink(link) || isDownloadLink(link)
}

function getButtonText(button) {
  if (button.tagName.toLowerCase() === 'input') {
    return normalizeText(button.value || '')
  }

  return normalizeText(button.textContent || button.getAttribute('aria-label') || button.getAttribute('title') || '')
}

function isSubmitButton(button) {
  const type = normalizeText(button.getAttribute('type') || '').toLowerCase()
  return type === 'submit' || button.hasAttribute('data-submit-action') || button.classList.contains('fui-submit')
}

function shouldTrackButton(button) {
  if (isSubmitButton(button)) {
    return false
  }

  if (button.closest('.fui-form')) {
    return false
  }

  return hasButtonClass(button)
}

/**
 * Initialize delegated high-value click tracking.
 * @param {Object} trackingStore - Store with getCid/getUtmParams methods
 * @param {Object} config - Tracking config
 */
export default function initInteractionTracking(trackingStore, {
  siteHandle = 'default',
  environment = 'dev',
  debugTracking = false,
}) {

  const config = {
    siteHandle,
    environment,
    debugTracking,
  }

  if (typeof teardown === 'function') {
    teardown()
  }

  const onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null
    if (!target) {
      return
    }

    const link = target.closest('a[href]')

    if (link && shouldTrackLink(link)) {
      const href = link.getAttribute('href') || ''
      let url = null

      try {
        url = new URL(href, window.location.origin)
      } catch {
        url = null
      }

      pushDataLayerEvent({
        event: 'ev_link_click',
        ...getTrackingContext(trackingStore),
        link_text: getLinkText(link),
        link_url: url ? url.href : href,
        link_domain: url ? url.hostname : '',
        link_is_outbound: Boolean(url && url.origin !== window.location.origin),
        link_is_download: isDownloadLink(link),
        link_target: normalizeText(link.getAttribute('target') || '_self'),
        click_context: getClickContext(link),
      }, config)

      return
    }

    const button = target.closest('button, input[type="button"], [role="button"]')

    if (!button || !shouldTrackButton(button)) {
      return
    }

    pushDataLayerEvent({
      event: 'ev_button_click',
      ...getTrackingContext(trackingStore),
      button_text: getButtonText(button),
      button_type: normalizeText(button.getAttribute('type') || 'button'),
      click_context: getClickContext(button),
    }, config)
  }

  document.addEventListener('click', onClick, true)

  teardown = () => {
    document.removeEventListener('click', onClick, true)
    teardown = null
  }

  return this
}
